import { faPencil } from '@fortawesome/pro-regular-svg-icons/faPencil';
import {
  ContestState,
  ScheduleModel
} from '@maxpreps/apiserviceclients/apiModels';
import { ROLE } from '@maxpreps/shared/constants/authentication';
import SportName from '@maxpreps/shared/constants/sportName';
import useRequestContext from '@maxpreps/shared/contexts/RequestContext';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import { getWebFormsTeamAdminUrl } from '@maxpreps/shared/functions/utils/getWebFormsAdminUrl';
import { hasPermission } from '@maxpreps/shared/functions/utils/userUtils';
import useIsAdmin from '@maxpreps/shared/hooks/useIsAdmin';
import { IConfigurableLink } from '@maxpreps/shared/models/configurableLink';
import { useUserRoles } from '@maxpreps/shared/redux/user/userHooks';
import THEME from '@maxpreps/shared/styles/colors';
import Icon from '@maxpreps/shared/ui/Icon/Icon';
import OverflowMenu from '@maxpreps/shared/ui/OverflowMenu/OverflowMenu';
import React from 'react';
import getCSRRewriteAttributes from '../../../utils/getCSRRewriteAttributes';
import userCanEnterScores from '../../../utils/schedule/userCanEnterScores';
import useScheduleContext from '../ScheduleContext';
import {
  getAdminEnterScoresLink,
  getAdminEnterStatsLink,
  getTeamUrl
} from './../../../utils/navigation';
import { Page } from './../../../utils/pages';

const getMenuItems = (
  contest,
  teamContext,
  scheduleContext,
  roles,
  isAdmin,
  appType
) => {
  const {
    contestId,
    contestAlias,
    contestState,
    currentTeam,
    isDeleted,
    rolesWhoCanEnterScores,
    reasonWhyCannotEnterScores,
    bracketGamesInMatchup
  } = contest;

  const { hasStats } = currentTeam;
  const {
    deleteContests,
    restoreContests,
    rebuildGameStory,
    convertToSingleGame,
    convertToSeries
  } = scheduleContext;
  const { teamId, sportSeasonId, canonicalUrl, allSeasonId } = teamContext.data;
  const { scheduleModel: scheduleModelKey } =
    teamContext.sportData as never as {
      scheduleModel: keyof ScheduleModel;
    };

  const isStandardAdmin =
    isAdmin && hasPermission([{ roleId: ROLE.standardAdmin }], roles);

  const canEnterScore = userCanEnterScores(
    roles,
    teamId,
    allSeasonId,
    rolesWhoCanEnterScores,
    reasonWhyCannotEnterScores
  );

  const menuItems: IConfigurableLink[] = [];

  // Restore Game
  if (isDeleted) {
    menuItems.push({
      text: `Restore ${contestAlias}`,
      as: 'button',
      asOptions: {
        onClick: () => restoreContests([contestId])
      }
    });
  }

  if (!isDeleted) {
    // Edit Game
    menuItems.push({
      text: `Edit ${contestAlias}`,
      as: 'a',
      asOptions: getCSRRewriteAttributes(
        getTeamUrl(Page.Schedule_Edit, canonicalUrl, {
          contestid: contestId
        }),
        teamId,
        sportSeasonId,
        allSeasonId,
        appType,
        undefined,
        ScheduleModel[scheduleModelKey]
      )
    });

    // Enter Score
    menuItems.push({
      text: `${
        contestState === ContestState.Boxscore ? 'Edit' : 'Enter'
      } Score`,
      isDisabled: !canEnterScore,
      as: 'a',
      asOptions: {
        href: getAdminEnterScoresLink(
          teamId,
          allSeasonId,
          sportSeasonId,
          contestId
        )
      }
    });

    // Enter Stats
    menuItems.push({
      text: `${hasStats ? 'Edit' : 'Enter'} Stats`,
      isDisabled: contestState !== ContestState.Boxscore,
      as: 'a',
      asOptions: {
        href: getAdminEnterStatsLink(
          teamId,
          allSeasonId,
          sportSeasonId,
          contestId
        )
      }
    });
  }

  // View Log
  if (isStandardAdmin) {
    menuItems.push({
      text: `View Log`,
      as: 'a',
      asOptions: {
        href: getWebFormsTeamAdminUrl(
          `/admin/coach/schedule/log.aspx?id=${contestId}`,
          teamId,
          allSeasonId,
          sportSeasonId
        )
      }
    });
  }

  if (contestState >= ContestState.ContestInProgress) {
    menuItems.push({
      text: `In Game`,
      as: 'a',
      asOptions: {
        href: getWebFormsTeamAdminUrl(
          `/admin/coach/schedule/manage_ingame_info.aspx?id=${contestId}`,
          teamId,
          allSeasonId,
          sportSeasonId
        )
      }
    });
  }

  // Game Story
  if (isAdmin) {
    menuItems.push({
      text: `Game Story`,
      as: 'button',
      asOptions: {
        onClick: () => rebuildGameStory(contestId)
      }
    });
  }

  // Delete Game
  if (!isDeleted) {
    menuItems.push({
      text: `Delete ${contestAlias}`,
      as: 'button',
      asOptions: {
        onClick: () => deleteContests([contestId])
      }
    });
  }

  // Baseball and Softball Series Conversions.
  if (
    bracketGamesInMatchup &&
    (teamContext.sportData.name === SportName.Baseball ||
      teamContext.sportData.name === SportName.Softball)
  ) {
    const isSingleGame = bracketGamesInMatchup === 1;
    menuItems.push({
      text: `Convert to ${isSingleGame ? 'Series' : 'Single'} Game`,
      as: 'button',
      asOptions: {
        onClick: () =>
          isSingleGame
            ? convertToSeries(contestId)
            : convertToSingleGame(contestId)
      }
    });
  }

  return menuItems;
};

const ScheduleEditDropdown = ({ contest }) => {
  const teamContext = useTeamContext();
  const scheduleContext = useScheduleContext();
  const isAdmin = useIsAdmin();
  const roles = useUserRoles();
  const { appType } = useRequestContext();

  const menuItems = getMenuItems(
    contest,
    teamContext,
    scheduleContext,
    roles,
    isAdmin,
    appType
  );

  return (
    <OverflowMenu menuItems={menuItems}>
      <Icon icon={faPencil} color={THEME.other.link} size={14} />
    </OverflowMenu>
  );
};

const ScheduleEditCell = ({ contest }) => (
  <td>
    <ScheduleEditDropdown contest={contest} />
  </td>
);

export default ScheduleEditCell;
